import React from "react";
import Input from "@/components/common/Form/Input";
import Button from "@/components/common/Button";
import SwitchToggle from "@/components/common/Form/Switch";
import { Table, Th, Td } from "@/components/Table";
import { Icon } from "@/components/Icons";
import { DocumentLineModel, ProductSearchItem } from "../types";
import ProductLookup from "./ProductLookup";

interface ProductTableProps {
  lines: DocumentLineModel[];
  filteredLines: DocumentLineModel[];
  productResults: ProductSearchItem[];
  showPantryList: boolean;
  validLines: DocumentLineModel[];
  selectedProductIndex: number;
  productDropdownPosition: {
    top: number;
    left: number;
    width: number;
    openUpward: boolean;
  } | null;
  focusedRowIndex: number | null;
  productInputRef: React.RefObject<HTMLDivElement>;
  inputRefs: React.MutableRefObject<{
    [key: string]: HTMLInputElement | null;
  }>;
  selectedProductRowRef: React.RefObject<HTMLTableRowElement | null>;
  justClosedDropdownRef: React.MutableRefObject<boolean>;
  isLoadingProducts?: boolean;
  disabled?: boolean;
  onShowPantryListChange: (value: boolean) => void;
  onLineChange: (index: number, payload: Partial<DocumentLineModel>) => void;
  onProductPick: (product: ProductSearchItem) => void;
  onAddProduct: () => void;
  onRemoveLine: (index: number) => void;
  onSetActiveProductRow: (index: number | null) => void;
  onSetProductSearchTerm: (term: string) => void;
  onSetProductResults: (results: ProductSearchItem[]) => void;
  onSetProductDropdownPosition: (
    position: {
      top: number;
      left: number;
      width: number;
      openUpward: boolean;
    } | null
  ) => void;
  onSetSelectedProductIndex: (index: number) => void;
  onSetFocusedRowIndex: (index: number | null) => void;
  onQuantityKeyDown: (
    e: React.KeyboardEvent<HTMLInputElement>,
    rowIndex: number
  ) => void;
  onCommentKeyDown: (
    e: React.KeyboardEvent<HTMLInputElement>,
    rowIndex: number
  ) => void;
}

const ProductTable: React.FC<ProductTableProps> = ({
  lines,
  filteredLines,
  productResults,
  showPantryList,
  validLines,
  selectedProductIndex,
  productDropdownPosition,
  productInputRef,
  inputRefs,
  selectedProductRowRef,
  justClosedDropdownRef,
  isLoadingProducts = false,
  disabled = false,
  onShowPantryListChange,
  onLineChange,
  onProductPick,
  onAddProduct,
  onRemoveLine,
  onSetActiveProductRow,
  onSetProductSearchTerm,
  onSetProductResults,
  onSetProductDropdownPosition,
  onSetSelectedProductIndex,
  onSetFocusedRowIndex,
  onQuantityKeyDown,
  onCommentKeyDown,
}) => {
  const handleLineChange = (
    index: number,
    payload: Partial<DocumentLineModel>
  ) => {
    onLineChange(index, payload);
  };

  const handleProductPick = (product: ProductSearchItem) => {
    onProductPick(product);
  };

  const handleAddProduct = () => {
    onAddProduct();
  };

  const removeLine = (index: number) => {
    onRemoveLine(index);
  };

  return (
    <>
      <div className="bg-white border border-neutral-20 rounded shadow-sm p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SwitchToggle
              checked={showPantryList}
              onChange={() => onShowPantryListChange(!showPantryList)}
              label="Pantry List"
              labelPosition="front"
              disabled={disabled}
              tabIndex={-1}
            />
          </div>
          <div className="text-base text-neutral-60">
            {validLines.length} active lines
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table className="w-auto">
            <thead>
              <tr>
                <Th className="whitespace-nowrap w-[100px] border-t border-l ">
                  SKU
                </Th>
                <Th className="whitespace-nowrap min-w-[100px] border-t border-l ">
                  Product Name
                </Th>
                <Th className="whitespace-nowrap w-[80px] border-t border-l ">
                  Unit
                </Th>
                <Th className="whitespace-nowrap w-[120px] border-t border-l ">
                  Quantity
                </Th>
                <Th className="whitespace-nowrap w-[275px] border-t border-l ">
                  Comment
                </Th>
                <Th className="whitespace-nowrap w-[80px] border-t border-l ">
                  Tax Code
                </Th>
                <Th className="whitespace-nowrap w-[80px] border-t border-l ">
                  Tax %
                </Th>
                <Th className="whitespace-nowrap w-[95px] border-t border-l ">
                  Price
                </Th>
                <Th className="whitespace-nowrap w-[100px] text-center border-t border-l border-r">
                  Actions
                </Th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Keep search row at top, display rest in order (newest first)
                const searchRow = filteredLines[0];
                const productRows = filteredLines.slice(1);
                const displayLines = [searchRow, ...productRows];

                return displayLines.map((line) => {
                  // Find original index for this line in the full lines array
                  const originalIndex = lines.findIndex(
                    (l) => l.id === line.id
                  );
                  const isSearchRow = originalIndex === 0;

                  return (
                    <tr key={line.id}>
                      <Td className="align-middle text-center border-b border-l p-1">
                        {isSearchRow ? (
                          <div className="text-neutral">
                            {line.productSKU || ""}
                          </div>
                        ) : (
                          <div className="text-neutral">
                            {line.productSKU || ""}
                          </div>
                        )}
                      </Td>
                      <Td className="align-middle min-w-[300px] border-b border-l p-1">
                        {isSearchRow ? (
                          <ProductLookup
                            value={line.productName}
                            productResults={productResults}
                            selectedProductIndex={selectedProductIndex}
                            dropdownPosition={productDropdownPosition}
                            isLoadingProducts={isLoadingProducts}
                            disabled={disabled}
                            inputRef={productInputRef}
                            selectedProductRowRef={selectedProductRowRef}
                            justClosedDropdownRef={justClosedDropdownRef}
                            onChange={(value) => {
                              handleLineChange(originalIndex, {
                                productName: value,
                              });
                              onSetProductSearchTerm(value);
                            }}
                            onFocus={() => onSetFocusedRowIndex(originalIndex)}
                            onKeyDown={(e) => {
                              if (
                                (e.key === "Enter" || e.key === "Tab") &&
                                productResults.length > 0 &&
                                selectedProductIndex >= 0
                              ) {
                                // If dropdown is open, select the product (Enter or Tab)
                                e.preventDefault();
                                handleProductPick(
                                  productResults[selectedProductIndex]
                                );
                              } else if (e.key === "Enter" && line.productId) {
                                // If product is already selected in search row, add it
                                e.preventDefault();
                                handleAddProduct();
                              }
                            }}
                            onProductPick={handleProductPick}
                            onSetActiveRow={(active) =>
                              onSetActiveProductRow(
                                active ? originalIndex : null
                              )
                            }
                            onSetProductResults={onSetProductResults}
                            onSetDropdownPosition={onSetProductDropdownPosition}
                            onSetSelectedIndex={onSetSelectedProductIndex}
                          />
                        ) : (
                          <div className="text-neutral">
                            {line.productName || ""}
                          </div>
                        )}
                      </Td>
                      <Td className="align-middle border-b border-l p-1">
                        {isSearchRow ? (
                          <div className="text-neutral">
                            {line.unitOfMeasure || "-"}
                          </div>
                        ) : (
                          <div className="text-neutral">
                            {line.unitOfMeasure || "-"}
                          </div>
                        )}
                      </Td>
                      <Td className="align-middle border-b border-l p-1">
                        {isSearchRow ? (
                          <Input
                            type="number"
                            min="0"
                            value={
                              line.quantity === 0 || !line.quantity
                                ? ""
                                : line.quantity
                            }
                            onChange={(e) =>
                              handleLineChange(originalIndex, {
                                quantity:
                                  e.target.value === ""
                                    ? 0
                                    : Number(e.target.value),
                              })
                            }
                            onFocus={() => onSetFocusedRowIndex(originalIndex)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddProduct();
                              } else {
                                onQuantityKeyDown(e, originalIndex);
                              }
                            }}
                            ref={(el) => {
                              inputRefs.current[`${originalIndex}-quantity`] =
                                el;
                            }}
                            disabled={disabled}
                            className="py-0.5 focus:border-[1px] focus:ring-0"
                          />
                        ) : (
                          <Input
                            type="number"
                            min="0"
                            value={line.quantity === 0 ? "" : line.quantity}
                            onChange={(e) =>
                              handleLineChange(originalIndex, {
                                quantity:
                                  e.target.value === ""
                                    ? 0
                                    : Number(e.target.value),
                              })
                            }
                            onFocus={() => onSetFocusedRowIndex(originalIndex)}
                            onKeyDown={(e) =>
                              onQuantityKeyDown(e, originalIndex)
                            }
                            ref={(el) => {
                              inputRefs.current[`${originalIndex}-quantity`] =
                                el;
                            }}
                            disabled={disabled}
                            className="py-0.5 focus:border-[1px] focus:ring-0"
                          />
                        )}
                      </Td>
                      <Td className="align-middle border-b border-l p-1">
                        {isSearchRow ? (
                          <Input
                            value={line.comment}
                            placeholder="Comment"
                            onChange={(e) =>
                              handleLineChange(originalIndex, {
                                comment: e.target.value,
                              })
                            }
                            onFocus={() => onSetFocusedRowIndex(originalIndex)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddProduct();
                              } else {
                                onCommentKeyDown(e, originalIndex);
                              }
                            }}
                            ref={(el) => {
                              inputRefs.current[`${originalIndex}-comment`] =
                                el;
                            }}
                            disabled={disabled}
                            className="py-0.5"
                          />
                        ) : (
                          <Input
                            value={line.comment}
                            placeholder="Comment"
                            onChange={(e) =>
                              handleLineChange(originalIndex, {
                                comment: e.target.value,
                              })
                            }
                            onFocus={() => onSetFocusedRowIndex(originalIndex)}
                            onKeyDown={(e) =>
                              onCommentKeyDown(e, originalIndex)
                            }
                            ref={(el) => {
                              inputRefs.current[`${originalIndex}-comment`] =
                                el;
                            }}
                            disabled={disabled}
                            className="py-0.5"
                          />
                        )}
                      </Td>
                      <Td className="align-middle border-b border-l p-1">
                        {isSearchRow ? (
                          <div className="text-neutral">
                            {line.taxCode || ""}
                          </div>
                        ) : (
                          <div className="text-neutral">
                            {line.taxCode || ""}
                          </div>
                        )}
                      </Td>
                      <Td className="align-middle border-b border-l p-1">
                        {isSearchRow ? (
                          <div className="text-neutral">
                            {line.taxPercentage
                              ? `${line.taxPercentage}%`
                              : "0.00"}
                          </div>
                        ) : (
                          <div className="text-neutral">
                            {line.taxPercentage
                              ? `${line.taxPercentage}%`
                              : "0.00"}
                          </div>
                        )}
                      </Td>
                      <Td className="align-middle border-b border-l p-1">
                        <div className="text-neutral px-2 py-1">
                          {line.sellPrice?.toFixed(2) || "0.00"}
                        </div>
                      </Td>
                      <Td className="align-middle text-center border-b border-l border-r p-1">
                        {isSearchRow ? (
                          line.productId ? (
                            <Button
                              size="xs"
                              color="blue"
                              variant="outline"
                              iconLeft={Icon.PlusBorder}
                              onClick={handleAddProduct}
                              disabled={disabled}
                            />
                          ) : (
                            <div className="text-neutral-60 text-base">-</div>
                          )
                        ) : (
                          <Button
                            size="xs"
                            color="red"
                            variant="outline"
                            iconLeft={Icon.Trash}
                            onClick={() => removeLine(originalIndex)}
                            disabled={disabled}
                          />
                        )}
                      </Td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </Table>
        </div>
      </div>
    </>
  );
};

export default ProductTable;
